// No import changes
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setCurrentCategoryData, setRelatedProducts } from "../../store/slice";
import { fetchRelaPro } from "../util";
import Breadcrumb from "../Breadcrumb";
import config, { baseURl } from "../../config";
import "./ProductPage.css";

const ProductsSection = () => {
  const location = useLocation();
  const { categorySlug, subCategorySlug } = useParams();
  const sectionRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { products } = useSelector((state) => state.data);
  const { catDropdown } = useSelector((state) => state.data);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (products?.categories?.length > 0) {
      setCategories(products.categories);

      const foundCategory =
        location.state?.category ||
        products.categories.find((cat) => cat.categorySlug === categorySlug);
      setSelectedCategory(foundCategory);

      if (foundCategory?.subCat?.length > 0) {
        const foundSubCat =
          location.state?.subCategory ||
          foundCategory.subCat.find(
            (sub) => sub.subCategorySlug === subCategorySlug
          );
        setSelectedSubCategory(foundSubCat || null);
      } else {
        setSelectedSubCategory(null);
      }
    }
  }, [products, location.state, categorySlug, subCategorySlug]);


  useEffect(() => {
    // console.log(catDropdown)
  }, [catDropdown])

  const imagePath = (cat) => {
    const matchedDropdown = catDropdown?.find((category) => category.slug === cat);
    if (matchedDropdown?.categoryImage) {
      return `${baseURl}/${matchedDropdown.categoryImage}`;
    }

    const matchedCategory = categories.find((category) => category.categorySlug === cat);
    const fallbackProductImage =
      matchedCategory?.products?.find((product) => product.productImage)?.productImage ||
      matchedCategory?.subCat
        ?.flatMap((sub) => sub.products || [])
        .find((product) => product.productImage)?.productImage;

    if (fallbackProductImage) {
      return `${baseURl}/${fallbackProductImage}`;
    }

    return config.logo;
  };

  const productImagePath = (imageFile) => {
    if (!imageFile) {
      return config.logo;
    }
    return `${baseURl}/${imageFile}`;
  };

  const handleCategoryClick = (category) => {
    const catSlug = category?.categorySlug;
    dispatch(setCurrentCategoryData({ cat: catSlug, subCat: "" }));
    navigate(`/product-page/${catSlug}`, {
      state: { category },
    });

    setSelectedCategory(category);
    setSelectedSubCategory(null);
    sectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubCategoryClick = (subCat) => {
    const catSlug = selectedCategory?.categorySlug;
    const subCatSlug = subCat?.subCategorySlug;
    dispatch(setCurrentCategoryData({ cat: catSlug, subCat: subCatSlug }));
    navigate(`/product-page/${catSlug}/${subCatSlug}`, {
      state: {
        category: selectedCategory,
        subCategory: subCat,
      },
    });

    setSelectedSubCategory(subCat);
    sectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleProductSelect = (product) => {
    if (!selectedCategory) return;

    const catSlug = selectedCategory.categorySlug;
    const subCatSlug = selectedSubCategory
      ? selectedSubCategory.subCategorySlug
      : "";

    dispatch(
      setCurrentCategoryData({
        cat: catSlug,
        subCat: subCatSlug,
      })
    );

    dispatch(
      setRelatedProducts(
        fetchRelaPro(products, catSlug, subCatSlug, product.productSlug)
      )
    );

    const route = subCatSlug
      ? `/Products/${catSlug}/${subCatSlug}/${product.productSlug}`
      : `/Products/${catSlug}/${product.productSlug}`;

    navigate(route, { state: { product } });
  };

  return (
    <section id="products-section28" className="products-section" ref={sectionRef}>
      <div className="products-container">
        <div className="products-grid">
          {/* Category cards */}
          {!selectedCategory &&
            categories.map((category, index) => (
              <div key={index} className="product-card-wrapper">
                <div
                  className="product-card"
                  onClick={() => handleCategoryClick(category)}
                >
                  <img
                    src={imagePath(category?.categorySlug)}
                    alt={category?.category}
                    className="product-img"
                  />
                  <div className="card-content">
                    <h3>{category?.category}</h3>
                    <p className="learn-more-text">Explore More</p>
                  </div>
                </div>
              </div>
            ))}

          {/* Subcategory cards */}
          {selectedCategory && !selectedSubCategory &&
            selectedCategory?.subCat?.length > 0 && (
              <>
                {selectedCategory.subCat.map((subCat, index) => (
                  <div key={index} className="product-card-wrapper">
                    <div
                      className="product-card"
                      onClick={() => handleSubCategoryClick(subCat)}
                    >
                      <img
                        src={imagePath(subCat?.subCategorySlug)}
                        alt={subCat?.subCatName}
                        className="product-img"
                      />
                      <div className="card-content">
                        <h3>{subCat?.subCatName}</h3>
                        <p className="learn-more-text">Explore More</p>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

          {/* Products in selected subcategory */}
          {selectedSubCategory &&
            selectedSubCategory?.products?.map((product, index) => (
              <div key={index} className="product-card-wrapper">
                <div
                  className="product-card"
                  onClick={() => handleProductSelect(product)}
                >
                  <img
                    src={productImagePath(product.productImage)}
                    alt={product?.prodName}
                    className="product-img"
                  />
                  <div className="card-content">
                    <h3>{product?.prodName}</h3>
                    <p className="learn-more-text">Explore More</p>
                  </div>
                </div>
              </div>
            ))}

          {/* Products in category with no subcategory */}
          {selectedCategory && !selectedSubCategory &&
            selectedCategory?.products?.length > 0 && (
              <>
                {selectedCategory.products.map((product, index) => (
                  <div key={index} className="product-card-wrapper">
                    <div
                      className="product-card"
                      onClick={() => handleProductSelect(product)}
                    >
                      <img
                        src={productImagePath(product.productImage)}
                        alt={product?.prodName}
                        className="product-img"
                      />
                      <div className="card-content">
                        <h3>{product?.prodName}</h3>
                        <p className="learn-more-text">Explore More</p>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

          {/* No subcategories or products */}
          {selectedCategory && !selectedSubCategory &&
            (!selectedCategory.subCat?.length &&
              !selectedCategory.products?.length) && (
              <div className="no-content">
                <p>No subcategories or products available for this category.</p>
              </div>
            )}
        </div>
      </div>
    </section>
  );
};

export default ProductsSection;